import axios from 'axios';

export type PriceBookPinnedBy = 'designer' | 'quote' | 'presentation' | 'rule';

export type BlueprintPricePin = {
	version: number | null;
	priceBookVersionId: string | null;
	priceBookPinnedBy: PriceBookPinnedBy | null;
	priceBookRuleId: string | null;
};

export type PatchPricePinParams = {
	email: string,
	designName: string,
	/** kitchen version to pin; null addresses the draft version */
	version: number | null,
	/** published version to price against; null clears the pin and follows current */
	priceBookVersionId: string | null,
	/** who decided: the chip is 'designer', generating a design package is 'presentation' */
	priceBookPinnedBy?: PriceBookPinnedBy,
	/** the targeting rule the version came from, when the pin is adopting a rule's answer */
	priceBookRuleId?: string | null,
	getToken: () => Promise<string>
};

// Every pin written from the tool is a hard pin, which is what stops a rule moving the design later.
export const patchPricePin = async (params: PatchPricePinParams): Promise<[BlueprintPricePin | null, string | null]> => {
	const { email, designName, version, priceBookVersionId, priceBookPinnedBy = 'designer', priceBookRuleId = null, getToken } = params;

	try {
		const token = await getToken();
		const response = await axios.patch<BlueprintPricePin>(
			`${import.meta.env.VITE_API_URL}/customers/${encodeURIComponent(email)}/blueprints/${encodeURIComponent(designName)}/price-pin`,
			{
				version,
				priceBookVersionId,
				// Kept on a cleared pin: 'designer' with no version is a deliberate follow-current, and losing
				// that provenance is what let a targeting rule silently reclaim the design.
				priceBookPinnedBy,
				priceBookRuleId: priceBookVersionId === null ? null : priceBookRuleId
			},
			{ headers: { Authorization: `Bearer ${token}` } }
		);
		return [response.data, null];
	} catch {
		return [null, 'Failed to save the price book selection'];
	}
};
